import { useEffect, useRef, useState } from "react";
import { PaginationState } from "web/react/components/feeds-filters/feeds-filters-pagination/pagination-state";

export function useFilterScrollPosition(
    scrollAreaRef: React.RefObject<HTMLDivElement>,
    paginationState: PaginationState,
    insideFilterScroll: boolean
): {
    onItemExpanded: (itemIndex: number) => void;
    containerRef: React.RefObject<HTMLOListElement>;
} {
    const [containerClosedHeight, setContainerClosedHeight] = useState(0);
    const containerRef = useRef<HTMLOListElement>(null);
    const [lastClickedIndex, setLastClickedIndex] = useState(0);

    // Keep the height of the container up to date
    // This happens every time the pagination state changes so that if the
    //  height of the list changes (eg new facet) then it will become eventually
    //  consistent with scrolling.
    useEffect(() => {
        if (
            !insideFilterScroll ||
            !containerRef.current ||
            // If there is something expanded then the height will be wrong
            paginationState.hasActivePage()
        ) {
            return;
        }

        setContainerClosedHeight(containerRef.current.clientHeight);

        // The lint rule wants us to check on `containerRef` which is wrong
        // because it will always be the same object - we actually care about
        // the value of the ref.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationState, containerRef.current]);

    function onItemExpanded(itemIndex: number): void {
        // sanity check
        if (!containerRef.current || !scrollAreaRef.current) {
            return;
        }

        // If the item that will collapse is below the one that will expand, the
        // scroll doesn't need to change
        setLastClickedIndex(itemIndex);
        if (itemIndex < lastClickedIndex) {
            return;
        }

        let currentHeight = containerRef.current.clientHeight;
        // If there isn't anything open to collapse, we don't need to adjust the scrolling
        if (currentHeight == containerClosedHeight) {
            return;
        }

        let collapseHeight = currentHeight - containerClosedHeight;
        // Scroll up by the currently open amount to negate it when it disappears
        scrollAreaRef.current.scrollTop -= collapseHeight;
    }

    return {
        onItemExpanded,
        containerRef,
    };
}
